import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { Input, Checkbox, Popup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { getQueryFilter } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import PlusIcon from 'mdi-react/PlusIcon'
import { useNavigate } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import { useSearch } from '@/hooks/searchParams'
import useUser from '@/hooks/user'
import adminResources from '@/modules/admin/resources'
import type { Department } from '@/modules/admin/resources'

import queries from '../queries'

import { DepartmentModal } from './DepartmentModal'
import { DepartmentsList } from './DepartmentsList'

export const DepartmentsListPage: FC = () => {
  const user = useUser()
  const navigate = useNavigate()
  const translate = useTranslation()

  const [departmentModal, setDepartmentModal] = useState<{
    isOpen: boolean
    departmentId?: number
  }>({
    isOpen: false,
  })

  const [queryShowHidden, setQueryShowHidden] = useSearchParamsByKey('show-hidden')
  const [showHiddenFilter, setShowHiddenFilter] = useState(Boolean(queryShowHidden))

  const [queryShowAllCompanies, setQueryShowAllCompanies] = useSearchParamsByKey('all-companies')
  const [showAllCompaniesFilter, setAllCompaniesFilter] = useState(Boolean(queryShowAllCompanies))

  const { searchFilter, setSearchFilter } = useSearch()

  useEffect(() => {
    setShowHiddenFilter(queryShowHidden ? JSON.parse(queryShowHidden) : false)
    setAllCompaniesFilter(queryShowAllCompanies ? JSON.parse(queryShowAllCompanies) : false)
  }, [queryShowHidden, queryShowAllCompanies])

  const { data: departmentsList } = useQuery(
    queries.adminDepartmentsList(),
    adminResources.getDepartmentsList
  )

  const stringMatcher = (item: Department) => [
    item.code,
    item.name,
    item.project,
    item?.user?.firstName,
    item?.user?.lastName,
    item?.deputyUser?.firstName,
    item?.deputyUser?.lastName,
    item.customer.name,
  ]

  const departmentsFiltered =
    departmentsList?.filter(departmentsObject => {
      const isActive = !departmentsObject.inactive
      const isVisible = showHiddenFilter || (!showHiddenFilter && isActive)
      const customerId = departmentsObject.customerId

      // if the show showAllCompaniesFilter is null we display only departments that have the same customerId as the user
      const isCustomerFilterMet = showAllCompaniesFilter ? true : customerId === user?.customerId

      const isQueryFilterMet = getQueryFilter(searchFilter, stringMatcher)(departmentsObject)
      return isQueryFilterMet && isVisible && isCustomerFilterMet
    }) || []

  const departmentDetails =
    departmentsList && departmentModal.departmentId
      ? departmentsList.find(({ id }) => id === departmentModal.departmentId)
      : null

  if (!departmentsList) return null

  return (
    <Container onSwipedLeft={() => navigate('/')}>
      <Container.Header>
        <Translation id="Departments" />
      </Container.Header>

      <Button
        Icon={<PlusIcon />}
        className="mb-4 ml-0"
        onClick={() => setDepartmentModal({ isOpen: true })}
      >
        <Translation id="New department" />
      </Button>

      <div className="flex w-full flex-wrap items-center justify-start gap-2">
        <div>
          <Input
            id="department-search"
            value={searchFilter}
            placeholder={translate('Search')}
            onChange={e => setSearchFilter(e.target.value)}
          />
        </div>

        <Checkbox
          id="show-all-companies-input"
          marginTop={0}
          marginLeft={0}
          checked={queryShowAllCompanies ? JSON.parse(queryShowAllCompanies) : false}
          onChange={() => {
            const updatedShowHidden = queryShowAllCompanies
              ? JSON.parse(queryShowAllCompanies)
                ? 'false'
                : 'true'
              : 'true'
            setQueryShowAllCompanies(updatedShowHidden)
          }}
          label={<Translation id="All companies" />}
        />

        <Checkbox
          id="show-hidden-input"
          marginTop={0}
          marginLeft={0}
          checked={queryShowHidden ? JSON.parse(queryShowHidden) : false}
          onChange={() => {
            const updatedShowHidden = queryShowHidden
              ? JSON.parse(queryShowHidden)
                ? 'false'
                : 'true'
              : 'true'
            setQueryShowHidden(updatedShowHidden)
          }}
          label={<Translation id="Show hidden" />}
        />
      </div>

      <DepartmentsList
        departments={departmentsFiltered}
        searchFilter={searchFilter}
        onClickDepartmentRow={setDepartmentModal}
      />

      <Popup
        variant="narrow"
        ariaLabel={translate('Add or edit department')}
        isOpen={departmentModal.isOpen}
        onClose={() => setDepartmentModal({ isOpen: false })}
      >
        <DepartmentModal
          departmentDetails={departmentDetails || null}
          onClose={() => setDepartmentModal({ isOpen: false })}
          showAllCompanies={() => setQueryShowAllCompanies('true')}
          showHidden={() => setQueryShowHidden('true')}
        />
      </Popup>
    </Container>
  )
}
